import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplierRequest } from '../queryKeys';

import { fetchSupplierRequests } from './fetchSupplierRequests';
import {
  type SupplierRequestListResponse,
  type SupplierRequestsQueryParams,
} from './types';
import { zodSupplierRequestsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the supplier requests list query,
 * allowing the same options to be shared between the hook and route loaders.
 *
 * @param queryParameters - Pagination and filter parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSupplierRequestsQueryOptions = <
  TData = SupplierRequestListResponse,
>({
  queryParameters,
  options,
}: {
  queryParameters: Partial<SupplierRequestsQueryParams>;
  options?: Omit<
    UseQueryOptions<SupplierRequestListResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const validatedParams =
    zodSupplierRequestsQueryParamsSchema.parse(queryParameters);

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchSupplierRequests(validatedParams, { signal }),
    queryKey: queryKeysSupplierRequest.getAll(validatedParams),
    staleTime: ms('60s'),
    ...options,
  });
};

/**
 * React Query hook for fetching the paginated list of supplier requests.
 * Supports state filtering and pagination. Prefetches the next page for
 * improved perceived performance.
 *
 * @param queryParameters - Pagination and filter parameters.
 * @param options - Additional TanStack Query options.
 * @returns Paginated supplier request list.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Requests/operation/list_requests_vadmin_supplier_requests__get
 */
// fallow-ignore-next-line unused-export
export const useQuerySupplierRequests = <TData = SupplierRequestListResponse>(
  queryParameters: Partial<SupplierRequestsQueryParams>,
  options?: Omit<
    UseQueryOptions<SupplierRequestListResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  const nextPageOffset =
    (queryParameters.offset ?? 0) +
    (queryParameters.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);

  void queryClient.prefetchQuery(
    getSupplierRequestsQueryOptions({
      queryParameters: { ...queryParameters, offset: nextPageOffset },
    }),
  );

  return useQuery(
    getSupplierRequestsQueryOptions<TData>({ queryParameters, options }),
  );
};
